import cx from 'classnames';
import PropTypes from 'prop-types';

import { BlockedScoreTooltip } from 'src/components/BlockedScoreTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import Score, { SCORE_SIZE } from 'src/components/Score';
import { SectionScoreTooltipContent } from 'src/components/SectionScoreTooltipContent';
import { BLOCKED_REASON } from 'src/data/common';
import {
  formatToolDisplayName,
  getToolContentType,
  isFreestyleGenerationTool,
  isGeneralTextTool,
  isImageGenerationTool,
} from 'src/data/generationTool';
import { PROJECT_TYPE } from 'src/data/project';
import { SCORING_METHOD_TYPE } from 'src/data/scoring';
import { CHARACTER_BULLET } from 'src/lib/string';

import { useStyles } from './useStyles';

const ZERO_SCORE_TOOLTIP_TEXT = "We couldn't find any similar text variations.";

export const ScoreWithDetailsLink = ({
  className,
  score,
  expanded,
  locked,
  loading,
  isLoadingClassifiedGenerationTool,
  onClick,
  blockedReason,
  displayInsights,
  generationTool,
  scoringMethod,
  supportSize,
  assetType,
  isNonEnglishLanguage,
  tooltipOverride,
  multiPromptTool,
  enableSectionScoreTooltip,
  projectType,
  onScoreTooltipSelectTool,
  ...restProps
}) => {
  const styles = useStyles();
  const generationToolIcon =
    generationTool && !isFreestyleGenerationTool(generationTool) ? (
      <GenerationToolIcon
        className={styles.icon}
        tool={generationTool}
      />
    ) : null;

  const isSectionScoreTooltipActive =
    enableSectionScoreTooltip &&
    Boolean(generationTool) &&
    !tooltipOverride &&
    !isNonEnglishLanguage &&
    !isImageGenerationTool(generationTool);

  const tooltipText = (() => {
    if (isSectionScoreTooltipActive) {
      return '';
    }

    if (tooltipOverride) {
      return tooltipOverride.text;
    }

    const supportSizePart =
      supportSize != null
        ? scoringMethod?.type === SCORING_METHOD_TYPE.customModel
          ? `This score predicts the performance of this text compared to ${supportSize.toLocaleString()} similar texts from Anyword’s dataset and your connected datasets.`
          : `This score predicts the performance of this text compared to ${supportSize.toLocaleString()} similar texts from Anyword’s dataset.`
        : '';
    return scoringMethod?.type === SCORING_METHOD_TYPE.customModel
      ? `\n\n${supportSizePart}The score predicts either click-through or conversion rate, normalized to be between 0 and 100.`
      : `\n\n${supportSizePart}The score predicts either click-through or conversion rate, normalized to be between 0 and 100. Some product or service copy categories are more engaging than others and will likely start at higher baseline scores.`;
  })();

  const nonBlockedTitle = (() => {
    if (isNonEnglishLanguage) {
      return null;
    }

    if (tooltipOverride) {
      return tooltipOverride.title + tooltipText;
    }

    if (isSectionScoreTooltipActive) {
      return (
        <SectionScoreTooltipContent
          generationTool={generationTool}
          assetType={assetType}
          projectType={projectType}
          supportSize={supportSize}
          onSelectTool={onScoreTooltipSelectTool}
        />
      );
    }

    if (score === 0) {
      return ZERO_SCORE_TOOLTIP_TEXT;
    }

    const alignedTool =
      isImageGenerationTool(generationTool) && multiPromptTool ? multiPromptTool : generationTool;

    let tooltipTitle = '';
    if (isImageGenerationTool(generationTool)) {
      const title = formatToolDisplayName(alignedTool, { hasImage: true });
      tooltipTitle = `${title} ${CHARACTER_BULLET} Score`;
    } else {
      const title = formatToolDisplayName(alignedTool, { assetType });
      tooltipTitle = `${title} ${CHARACTER_BULLET} Score`;
    }
    return tooltipTitle + tooltipText;
  })();

  const showGeneralText =
    isSectionScoreTooltipActive &&
    (isFreestyleGenerationTool(generationTool) || isGeneralTextTool(generationTool));
  const generalTextLabel = isFreestyleGenerationTool(generationTool)
    ? 'General Text'
    : `${getToolContentType(generationTool)} General Text`;

  return (
    <BlockedScoreTooltip
      blockedReason={blockedReason}
      className={isSectionScoreTooltipActive ? styles.sectionScoreTooltipWide : null}
      nonBlockedTitle={nonBlockedTitle}
      placement="bottom"
      interactive={isSectionScoreTooltipActive}
    >
      <div
        className={cx(styles.root, className, {
          [styles.expanded]: expanded,
          [styles.locked]: locked,
          [styles.clickable]: Boolean(onClick),
        })}
        onClick={onClick}
        data-testid="scoreWithDetailsLink"
      >
        {showGeneralText ? (
          <CustomTypography
            data-testid="generalTextLabel"
            className={cx(styles.score, styles.generalTextLabel)}
            variant={TYPOGRAPHY_VARIANT.medium12}
          >
            {generalTextLabel}
          </CustomTypography>
        ) : (
          <Score
            className={styles.score}
            score={score}
            locked={locked}
            blockedReason={blockedReason}
            loading={loading || isLoadingClassifiedGenerationTool}
            size={SCORE_SIZE.large}
            generationToolIcon={generationToolIcon}
            {...restProps}
          />
        )}

        {displayInsights && (
          <CustomTypography
            className={styles.text}
            variant={TYPOGRAPHY_VARIANT.semiBold12}
          >
            Insights <span className={styles.arrow}>&rsaquo;</span>
          </CustomTypography>
        )}
      </div>
    </BlockedScoreTooltip>
  );
};

ScoreWithDetailsLink.propTypes = {
  className: PropTypes.string,
  score: PropTypes.number,
  expanded: PropTypes.bool,
  locked: PropTypes.bool,
  loading: PropTypes.bool,
  isLoadingClassifiedGenerationTool: PropTypes.bool,
  onClick: PropTypes.func,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
  displayInsights: PropTypes.bool,
  generationTool: PropTypes.string,
  supportSize: PropTypes.number,
  scoringMethod: PropTypes.shape({ type: PropTypes.string }),
  isNonEnglishLanguage: PropTypes.bool,
  tooltipOverride: PropTypes.shape({ title: PropTypes.string, text: PropTypes.string }),
  multiPromptTool: PropTypes.string,
  assetType: PropTypes.string,
  enableSectionScoreTooltip: PropTypes.bool,
  projectType: PropTypes.oneOf(Object.values(PROJECT_TYPE)),
  onScoreTooltipSelectTool: PropTypes.func,
};
ScoreWithDetailsLink.defaultProps = {
  className: null,
  score: null,
  expanded: false,
  locked: false,
  loading: false,
  isLoadingClassifiedGenerationTool: false,
  onClick: null,
  blockedReason: null,
  displayInsights: false,
  generationTool: null,
  supportSize: null,
  scoringMethod: null,
  isNonEnglishLanguage: false,
  tooltipOverride: null,
  multiPromptTool: null,
  assetType: null,
  enableSectionScoreTooltip: false,
  projectType: PROJECT_TYPE.longFormBuilder,
  onScoreTooltipSelectTool: null,
};
